import { format } from "date-fns";
import { useEffect, useMemo } from "react";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useTranslation } from "react-i18next";
import { NumericFormat } from "react-number-format";
import DateField from "../../../common/components/functional/dateField/DateField";
import DialogComponent from "../../../common/components/functional/dialogs/DialogComponent";
import DialogContentWrapper from "../../../common/components/functional/dialogs/DialogContentWrapper";
import InputHelperText from "../../../common/components/functional/error/InputHelperText";
import FormGridRow from "../../../common/components/functional/formGridRow/FormGridRow";
import { Input } from "../../talentProfile/editForms/salaries/CustomInputs";
import {
  OrderTemplateFormValues,
  SalaryLimitsFormValues,
} from "../types/types";

type Props = {
  open: boolean;
  onClose: () => void;
  index: number | null;
};

const SalaryLimitsForm = ({ open, onClose, index }: Props) => {
  const { t } = useTranslation("order");
  const {
    control: mainControl,
    watch,
    setValue,
  } = useFormContext<OrderTemplateFormValues>();

  const { append, update } = useFieldArray({
    control: mainControl,
    name: "salaryLimits",
  });

  const salaryLimits = watch("salaryLimits");

  const isEditMode = index !== null;
  const existingItem = isEditMode ? salaryLimits[index] : null;

  const defaultValues: SalaryLimitsFormValues = useMemo(
    () => ({
      date: existingItem?.date ? new Date(existingItem.date) : null,
      value: existingItem?.value || "",
    }),
    [existingItem],
  );

  const methods = useForm<SalaryLimitsFormValues>({
    defaultValues,
    mode: "onChange",
  });

  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors, isValid },
  } = methods;

  // TODO: can this be done more elegantly?
  useEffect(() => {
    if (open) {
      const resetValues: SalaryLimitsFormValues = {
        date: existingItem?.date ? new Date(existingItem.date) : null,
        value: existingItem?.value || "",
      };
      reset(resetValues);
    }
  }, [open, index, existingItem, reset]);

  const onSave = (data: SalaryLimitsFormValues) => {
    if (!data.date) return;

    const formattedDate = format(data.date, "yyyy-MM-dd");

    // Check for duplicate dates (excluding current item in edit mode)
    const isDuplicate = salaryLimits.some((item, idx) => {
      if (isEditMode && idx === index) return false;
      return item.date === formattedDate;
    });

    if (isDuplicate) {
      setError("date", {
        type: "manual",
        message: t("salaryLimitDateDuplicate"),
      });
      return;
    }

    const salaryLimit = {
      date: formattedDate,
      value: data.value,
    };

    if (isEditMode) {
      update(index, salaryLimit);
    } else {
      append(salaryLimit);
    }

    // Salary limits list is now the source of truth for salary limit
    setValue("salaryLimit", "");
    onClose();
  };

  const title = isEditMode ? t("editSalaryLimit") : t("addSalaryLimit");

  return (
    <DialogComponent
      open={open}
      onClose={onClose}
      sx={{ width: "400px" }}
      dragHandleSelector="drag-handle-salary-limits"
    >
      <FormProvider {...methods}>
        <DialogContentWrapper
          margin
          title={title}
          onCancel={onClose}
          onSave={handleSubmit(onSave)}
          saveDisabled={!isValid}
        >
          <FormGridRow
            labelSize={5}
            fieldSize={7}
            label={t("salaryLimitEffectiveDate")}
          >
            <DateField
              name="date"
              sx={{ maxWidth: "150px" }}
              error={!!errors.date}
              required={{ value: true, message: "" }}
              showHelperText={false}
            />
          </FormGridRow>
          <FormGridRow
            labelSize={5}
            fieldSize={7}
            label={t("salaryLimitValue")}
          >
            <Controller
              name="value"
              control={control}
              rules={{ required: true }}
              render={({ field: { value, onChange } }) => (
                <NumericFormat
                  value={value}
                  decimalSeparator=","
                  valueIsNumericString
                  onValueChange={(values) => onChange(values.value)}
                  suffix=" €/h"
                  placeholder="€/h"
                  customInput={Input}
                  decimalScale={2}
                  fixedDecimalScale
                  inputProps={{ style: { textAlign: "center" } }}
                  InputProps={{ style: { width: "150px" } }}
                />
              )}
            />
          </FormGridRow>
          <FormGridRow labelSize={5} fieldSize={7}>
            <InputHelperText
              errorMessage={errors.date?.message || errors.value?.message}
              TypographyProps={{ ml: "4px" }}
            />
          </FormGridRow>
        </DialogContentWrapper>
      </FormProvider>
    </DialogComponent>
  );
};

export default SalaryLimitsForm;
